import type { ReactNode, ComponentProps } from "react";

import { Query } from "util/graphql/query";
import { hardNavigateTo } from "util/navigation";
import { supportEmail } from "constants/support";
import { segmentTrack } from "util/segment";
import { SEGMENT_EVENTS } from "constants/analytics";

import ZendeskChat from "./zendesk";
import ViewerChatPropertiesQuery, {
  type ViewerChatProperties_viewer as Viewer,
} from "./chat_query.graphql";

type Props = {
  children?: ReactNode;
  onToggle?: ComponentProps<typeof ZendeskChat>["onToggle"];
  viewer: Viewer | null;
  showSupportButton?: boolean;
};

function SupportChat({ children, viewer, onToggle, showSupportButton = true }: Props) {
  return (
    <>
      {children}
      <ZendeskChat viewer={viewer} onToggle={onToggle} showSupportButton={showSupportButton} />
    </>
  );
}

/** Standalone chat that can do its own query for data */
export function StandaloneChat({ showSupportButton = true }: { showSupportButton?: boolean }) {
  return (
    <Query query={ViewerChatPropertiesQuery}>
      {({ data }) => {
        return data?.viewer ? (
          <SupportChat
            viewer={data.viewer.user ? data.viewer : null}
            showSupportButton={showSupportButton}
          />
        ) : null;
      }}
    </Query>
  );
}

function nonNullZe(zE: Window["zE"]): zE is Exclude<Window["zE"], undefined> {
  return Boolean(zE);
}

export function supportChatIsInitialized(): boolean {
  return nonNullZe(window.zE);
}

export function openSupportChat() {
  if (!supportChatIsInitialized()) {
    // In the case the user has an adblocker that blocks Zendesk
    // we fallback to the support email address.
    hardNavigateTo(`mailto:${supportEmail}`);
  }
  const { zE } = window;
  if (nonNullZe(zE)) {
    zE("webWidget", "show");
    zE("webWidget", "open");
    segmentTrack(SEGMENT_EVENTS.OPENED_SUPPORT_CHAT);
  }
}

export function hideSupportChat() {
  const { zE } = window;
  if (nonNullZe(zE)) {
    zE("webWidget", "hide");
  }
}

export default SupportChat;
